import { Theme, css } from '@emotion/react';

import { useTranslation } from 'next-i18next';
import { StaticImageData } from 'next/image';

import { useState } from 'react';

import { useLoadingStore } from '@stores/useLoadingStore';
import { FontSizes, Spacing } from 'chatie-design-system';

import { createBitlinks } from '@api/bitly';
import { Platform, ShareType } from '@constants';
import { useGlobalContext } from '@contexts/global';
import { useAnalytics } from '@utils/analytics';
import { useMe } from '@utils/hook';
import { toQueryString } from '@utils/misc';
import { useWebView } from '@utils/webview';

import facebookImg from '../../public/images/share/facebook.png';
import kakaoImg from '../../public/images/share/kakao.png';
import urlImg from '../../public/images/share/url.png';
import Image from '../common/Image';
import Modal, { ModalProps } from '../common/Modal';

/** API */
// export const postDynamicLink = async (longDynamicLink: string) => {
//   const { data } = await axios.post<{ shortLink: string }>(
//     `https://firebasedynamiclinks.googleapis.com/v1/shortLinks?key=${process.env.NEXT_PUBLIC_DYNAMIC_LINK_KEY}`,
//     { longDynamicLink }
//   );
//   return data;
// };

export type ShareSocial = 'facebook' | 'kakao' | 'url';

/**
 * Components
 */
type Props = {
  description?: string;
  url?: string;
  title?: string;
  summary?: string;
  imageUrl?: string;
  storyId?: number;
  episodeId?: number;
  shareType: 'honor' | 'story' | 'episode';
};

const wrapCSS = css`
  display: flex;
  justify-content: space-between;
  width: 240px;
  margin: 0 auto;

  button {
    width: 60px;
  }

  button p {
    margin-top: ${Spacing.xsmall}px;
  }
`;

const descriptionCSS = (theme: Theme) => css`
  color: ${theme.var.gray500};
  font-size: ${FontSizes.small}px;
  text-align: center;
  margin-bottom: ${Spacing.small}px;
`;

const ShareModal = ({
  description,
  url,
  title,
  summary,
  episodeId,
  storyId,
  shareType,
  imageUrl = 'https://chatie.me/og_image.png',
  ...props
}: Props & ModalProps) => {
  const { t } = useTranslation();

  const { web } = useWebView();
  const { firebase } = useAnalytics();

  const { platform } = useGlobalContext();
  const { data: me } = useMe();

  const [shortLinks, setShortLinks] = useState<{
    [key in ShareSocial]?: string;
  }>({});

  const { setLoading } = useLoadingStore();

  const socials: Array<{
    image: StaticImageData;
    label: string;
    type: ShareSocial;
  }> = [
    {
      image: kakaoImg,
      label: t('카카오'),
      type: 'kakao',
    },
    {
      image: facebookImg,
      label: t('페이스북'),
      type: 'facebook',
    },
    {
      image: urlImg,
      label: t('링크'),
      type: 'url',
    },
  ];

  const makeShortLink = async (type: ShareSocial, url: string) => {
    const isExternal = /^https?/.test(url);
    if (isExternal) {
      return Promise.resolve(url);
    }

    const destUrlParams = toQueryString({
      shareType: platform === Platform.web ? ShareType.web : ShareType.app,
      uid: me?.id,
      share: true,
      source: type,
    });

    const destUrl = `${process.env.NEXT_PUBLIC_SHARE_DOMAIN}${url}${destUrlParams}`;

    // const longDynamicLink =
    //   type === 'kakao'
    //     ? `${process.env.NEXT_PUBLIC_DYNAMIC_LINK_LEGACY_URL}${toQueryString(
    //         params
    //       )}`
    //     : `${process.env.NEXT_PUBLIC_DYNAMIC_LINK_URL}${toQueryString(params)}`;

    const res = await createBitlinks({ longUrl: destUrl, title: title });
    return res.link;
  };

  const shareViaKakao = (linkUrl: string) => {
    const kakaoId = process.env.NEXT_PUBLIC_KAKAO_APP_ID ?? '';
    if (!window?.Kakao?.isInitialized()) {
      window?.Kakao?.init(kakaoId);
    }

    window?.Kakao?.Share?.sendDefault({
      objectType: 'feed',
      content: {
        title: title,
        description: summary,
        imageUrl: imageUrl,
        link: {
          mobileWebUrl: linkUrl,
          webUrl: linkUrl,
        },
      },
      buttons: [
        {
          title: '자세히 보기',
          link: {
            mobileWebUrl: linkUrl,
            webUrl: linkUrl,
          },
        },
      ],
    });
  };

  const handleShare = (type: ShareSocial) => {
    if (url === undefined || title === undefined || summary === undefined) {
      return;
    }

    if (shortLinks[type]) {
      if (type === 'kakao') {
        return shareViaKakao(shortLinks[type] as string);
      }
      return web.share(
        type,
        shortLinks[type] as string,
        title,
        summary,
        imageUrl
      );
    }

    setLoading(true);

    makeShortLink(type, url)
      .then((shortLink) => {
        setShortLinks((prev) => ({ ...prev, [type]: shortLink }));
        firebase.logEvent('share_click', {
          type: shareType,
          url: url,
          source: type,
          storyId,
          episodeId,
        });
        if (type === 'kakao') {
          shareViaKakao(shortLink);
        } else {
          web.share(type, shortLink, title, summary, imageUrl);
        }
      })
      .catch(() => {
        alert(
          t('공유 링크 생성을 하지 못하였습니다. 잠시 후 다시 시도해 주세요.')
        );
      })
      .finally(() => setLoading(false));
  };

  return (
    <Modal {...props} title={t('공유하기')}>
      {description && (
        <p css={descriptionCSS} className="multiline">
          {description}
        </p>
      )}

      <div css={wrapCSS}>
        {socials.map((social) => {
          return (
            <button
              key={`share-${social.type}`}
              onClick={() => handleShare(social.type)}
            >
              <Image
                src={social.image}
                alt={social.label}
                width={36}
                height={36}
              />
              <p>{social.label}</p>
            </button>
          );
        })}
      </div>
    </Modal>
  );
};

export default ShareModal;
